import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MfaSetup, MfaStatus } from '@up2d8/contracts';
import { useSession } from '../../shared/session';
import { useProblemText } from '../../shared/use-problem-text';
import { Btn, Field, Form, Input, Note, Stack } from '../admin-kit';
import { QrImage, RecoveryCodes, groupedSecret } from './mfa-steps';

/**
 * Tweestapsverificatie in je profiel (FBK-12): instellen met een authenticator-app, nieuwe
 * herstelcodes, en uitzetten zolang je bedrijf het niet verplicht. Met de bouwstenen van het
 * beheer, binnen `a-scope`, zodat het in de app en in het beheer hetzelfde is.
 */
export function MfaSelfCard() {
  const { t } = useTranslation();
  const { api } = useSession();
  const text = useProblemText();
  const queryClient = useQueryClient();
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [action, setAction] = useState<'disable' | 'recovery' | null>(null);
  const [code, setCode] = useState('');

  const status = useQuery({
    queryKey: ['me', 'mfa'],
    queryFn: async (): Promise<MfaStatus | null> => {
      const { data } = await api.GET('/api/v1/me/mfa', {});
      return (data as MfaStatus | undefined) ?? null;
    },
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['me', 'mfa'] });
  const clean = () => code.replace(/\s/g, '');

  const start = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/me/mfa/setup', {});
      if (error || !data) throw error ?? new Error('Instellen mislukt');
      return data as MfaSetup;
    },
    onSuccess: (data) => {
      setSetup(data);
      setCode('');
    },
  });
  const confirm = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/me/mfa/confirm', {
        body: { code: clean() } as never,
      });
      if (error || !data) throw error ?? new Error('Bevestigen mislukt');
      return (data as { recoveryCodes: string[] }).recoveryCodes;
    },
    onSuccess: (list) => {
      setSetup(null);
      setCodes(list);
      void refresh();
    },
  });
  const act = useMutation({
    mutationFn: async () => {
      if (action === 'disable') {
        const { error } = await api.POST('/api/v1/me/mfa/disable', {
          body: { code: clean() } as never,
        });
        if (error) throw error;
        return null;
      }
      const { data, error } = await api.POST('/api/v1/me/mfa/recovery-codes', {
        body: { code: clean() } as never,
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return (data as { recoveryCodes: string[] }).recoveryCodes;
    },
    onSuccess: (list) => {
      setAction(null);
      setCode('');
      if (list) setCodes(list);
      void refresh();
    },
  });

  const data = status.data;
  if (!data) return null;

  const error = start.error ?? confirm.error ?? act.error;

  return (
    <div className="a-scope" data-testid="mfa-self">
      <Stack>
        <div>
          <strong>{t('mfa.selfTitle')}</strong>
          <div className="a-hint">
            {data.enabled
              ? t('mfa.selfOn', { count: data.recoveryCodesLeft })
              : data.required
                ? t('mfa.selfRequired')
                : t('mfa.selfOff')}
          </div>
        </div>

        {error ? (
          <Note tone="danger" role="alert">
            {text(error, t('errors.generic'))}
          </Note>
        ) : null}

        {codes ? (
          <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
        ) : setup ? (
          <Form onSubmit={() => confirm.mutate()}>
            <QrImage svg={setup.qrSvg} label={t('mfa.qrLabel')} />
            <div className="a-hint" style={{ textAlign: 'center' }}>
              {t('mfa.manualKey')} <span className="a-mono">{groupedSecret(setup.secret)}</span>
            </div>
            <Field label={t('mfa.codeLabel')}>
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  data-testid="mfa-self-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              )}
            </Field>
            <Btn type="submit" disabled={clean().length !== 6 || confirm.isPending}>
              {t('mfa.enrollConfirm')}
            </Btn>
          </Form>
        ) : action ? (
          <Form onSubmit={() => act.mutate()}>
            <Field label={t('mfa.codeLabel')}>
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              )}
            </Field>
            <div className="a-actions">
              <Btn
                type="submit"
                variant={action === 'disable' ? 'danger' : 'primary'}
                disabled={clean().length !== 6 || act.isPending}
              >
                {action === 'disable' ? t('mfa.disable') : t('mfa.newRecovery')}
              </Btn>
              <Btn variant="sec" onClick={() => setAction(null)}>
                {t('common.cancel')}
              </Btn>
            </div>
          </Form>
        ) : data.enabled ? (
          <div className="a-actions">
            <Btn variant="sec" onClick={() => setAction('recovery')}>
              {t('mfa.newRecovery')}
            </Btn>
            {data.required ? null : (
              <Btn variant="danger-sec" onClick={() => setAction('disable')}>
                {t('mfa.disable')}
              </Btn>
            )}
          </div>
        ) : (
          <div>
            <Btn testId="mfa-self-start" disabled={start.isPending} onClick={() => start.mutate()}>
              {t('mfa.enable')}
            </Btn>
          </div>
        )}
      </Stack>
    </div>
  );
}
